'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Environment {
  id: string;
  name: string;
  status: 'active' | 'maintenance' | 'offline';
}

interface EnvironmentSelectorProps {
  environments: Environment[];
  selectedEnvironment: string;
  onEnvironmentChange: (environmentId: string) => void;
}

const EnvironmentSelector = ({ 
  environments, 
  selectedEnvironment, 
  onEnvironmentChange 
}: EnvironmentSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentEnvironment = environments.find(env => env.id === selectedEnvironment);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success';
      case 'maintenance':
        return 'bg-warning';
      case 'offline':
        return 'bg-error';
      default:
        return 'bg-text-secondary';
    }
  };

  return (
    <div data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:58:4" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="58" data-component-end-line="101" data-component-file="EnvironmentSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative" ref={dropdownRef}>
      <button data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:59:6" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="59" data-component-end-line="74" data-component-file="EnvironmentSelector.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%20px-4%20py-2%20bg-secondary%20rounded-lg%20border%20border-border%20transition-smooth%20hover%3Abg-muted%20hover%3Aborder-accent-cyan%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-accent-cyan%20focus%3Aring-offset-2%20focus%3Aring-offset-surface%22%7D"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-4 py-2 bg-secondary rounded-lg border border-border transition-smooth hover:bg-muted hover:border-accent-cyan focus:outline-none focus:ring-2 focus:ring-accent-cyan focus:ring-offset-2 focus:ring-offset-surface"
        aria-label="Select environment"
        aria-expanded={isOpen}
      >
        <div data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:65:8" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="65" data-component-end-line="65" data-component-file="EnvironmentSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-2 h-2 rounded-full ${getStatusColor(currentEnvironment?.status || 'offline')}`} />
        <span data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:66:8" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="66" data-component-end-line="68" data-component-file="EnvironmentSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-body%20text-sm%20font-medium%20text-foreground%22%7D" className="font-body text-sm font-medium text-foreground">
          {currentEnvironment?.name || 'Select Environment'}
        </span>
        <Icon data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:69:8" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="69" data-component-end-line="73" data-component-file="EnvironmentSelector.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D" 
          name="ChevronDownIcon" 
          size={16} 
          className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:77:8" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="77" data-component-end-line="99" data-component-file="EnvironmentSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20mt-2%20w-64%20bg-popover%20rounded-lg%20border%20border-border%20shadow-navy-lg%20z-1010%20overflow-hidden%22%7D" className="absolute top-full left-0 mt-2 w-64 bg-popover rounded-lg border border-border shadow-navy-lg z-1010 overflow-hidden">
          <div data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:78:10" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="78" data-component-end-line="98" data-component-file="EnvironmentSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-2%22%7D" className="p-2">
            {environments.map((env) => (
              <button data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:80:14" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="80" data-component-end-line="96" data-component-file="EnvironmentSelector.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={env.id}
                onClick={() => {
                  onEnvironmentChange(env.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-smooth ${
                  selectedEnvironment === env.id
                    ? 'bg-secondary text-accent-cyan' :'text-popover-foreground hover:bg-muted'
                }`}
              >
                <div data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:91:16" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="91" data-component-end-line="91" data-component-file="EnvironmentSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-2 h-2 rounded-full ${getStatusColor(env.status)}`} />
                <span data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:92:16" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="92" data-component-end-line="92" data-component-file="EnvironmentSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-body%20text-sm%20flex-1%20text-left%22%7D" className="font-body text-sm flex-1 text-left">{env.name}</span>
                {selectedEnvironment === env.id && (
                  <Icon data-component-id="src/app/operations-command-center/components/EnvironmentSelector.tsx:94:18" data-component-path="src/app/operations-command-center/components/EnvironmentSelector.tsx" data-component-line="94" data-component-end-line="94" data-component-file="EnvironmentSelector.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%2C%22className%22%3A%22text-accent-cyan%22%7D" name="CheckIcon" size={16} className="text-accent-cyan" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentSelector;